import { RuntimeConfigService } from './runtime-config.service';
import { AppRuntimeConfig } from './app-runtime-config.model';

function normalizeBaseUrl(value: string | undefined | null): string {
  return String(value || '').trim().replace(/\/+$/, '');
}

function getBrowserOrigin(): string {
  if (
    typeof window !== 'undefined' &&
    window.location?.origin &&
    window.location.origin !== 'null'
  ) {
    return window.location.origin;
  }

  return '';
}

function isLocalOrigin(origin: string): boolean {
  return origin.includes('localhost') || origin.includes('127.0.0.1');
}

function shouldRejectLocalhostBaseUrl(candidateBaseUrl: string, browserOrigin: string): boolean {
  const normalizedCandidateBaseUrl = normalizeBaseUrl(candidateBaseUrl);
  const normalizedBrowserOrigin = normalizeBaseUrl(browserOrigin);

  if (!normalizedCandidateBaseUrl || !normalizedBrowserOrigin) {
    return false;
  }

  return !isLocalOrigin(normalizedBrowserOrigin) && isLocalOrigin(normalizedCandidateBaseUrl);
}

function sanitizeRuntimeConfig(data: AppRuntimeConfig, browserOrigin: string): AppRuntimeConfig {
  const normalizedBrowserOrigin = normalizeBaseUrl(browserOrigin);
  const backendBaseUrl = normalizeBaseUrl(data?.backendBaseUrl);
  const frontendBaseUrl = normalizeBaseUrl(data?.frontendBaseUrl || data?.frontendUrl);

  return {
    ...data,
    backendBaseUrl: shouldRejectLocalhostBaseUrl(backendBaseUrl, normalizedBrowserOrigin)
      ? normalizedBrowserOrigin
      : backendBaseUrl,
    frontendBaseUrl: shouldRejectLocalhostBaseUrl(frontendBaseUrl, normalizedBrowserOrigin)
      ? normalizedBrowserOrigin
      : frontendBaseUrl,
    frontendUrl: shouldRejectLocalhostBaseUrl(frontendBaseUrl, normalizedBrowserOrigin)
      ? normalizedBrowserOrigin
      : frontendBaseUrl,
  };
}

function buildCandidateUrls(): string[] {
  const origin = getBrowserOrigin();

  if (isLocalOrigin(origin)) {
    return [
      '/api/config/runtime-config',
      'http://localhost:3001/api/config/runtime-config',
    ];
  }

  if (origin) {
    return [
      '/api/config/runtime-config',
      `${normalizeBaseUrl(origin)}/api/config/runtime-config`,
    ];
  }

  return ['/api/config/runtime-config'];
}

type RuntimeConfigFetchAttempt = {
  url: string;
  config: AppRuntimeConfig | null;
  error: string | null;
};

function buildFallbackRuntimeConfig(): AppRuntimeConfig {
  const origin = normalizeBaseUrl(getBrowserOrigin());

  return {
    appName: 'The Gherkin Forge',
    backendBaseUrl: origin,
    frontendBaseUrl: origin,
    frontendUrl: origin,
    oauth: {
      jiraStartPath: '/api/jira/oauth/start',
      jiraStatusPath: '/api/jira/status',
      oauthRedirectPath: '/oauth/callback',
    },
    features: {
      jiraEnabled: true,
      auditEnabled: true,
      builderEnabled: true,
    },
    stripe: {
      enabled: false,
      publishableKey: '',
    },
    pricing: {
      publicPlans: [],
      flags: {
        showBeginnerTestPlan: false,
      },
      plans: {
        beginnerTest: {
          unitPriceEuros: 1,
        },
      },
    },
  };
}

async function fetchRuntimeConfig(url: string): Promise<RuntimeConfigFetchAttempt> {
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      return {
        url,
        config: null,
        error: `HTTP ${response.status}`,
      };
    }

    const data = await response.json() as AppRuntimeConfig;
    return {
      url,
      config: sanitizeRuntimeConfig({
        ...data,
        backendBaseUrl: normalizeBaseUrl(data?.backendBaseUrl),
        frontendBaseUrl: normalizeBaseUrl(data?.frontendBaseUrl),
        frontendUrl: normalizeBaseUrl(data?.frontendUrl),
      }, getBrowserOrigin()),
      error: null,
    };
  } catch (error) {
    return {
      url,
      config: null,
      error: error instanceof Error ? error.message : 'NETWORK_ERROR',
    };
  }
}

export function loadRuntimeConfig(configService: RuntimeConfigService) {
  return async () => {
    const candidates = buildCandidateUrls();
    const attempts: RuntimeConfigFetchAttempt[] = [];

    for (const candidate of candidates) {
      const attempt = await fetchRuntimeConfig(candidate);
      attempts.push(attempt);

      if (attempt.config) {
        configService.setConfig(attempt.config);
        return;
      }
    }

    const fallbackConfig = buildFallbackRuntimeConfig();
    configService.setConfig(fallbackConfig);

    console.error(
      'Runtime config could not be loaded from the backend. Falling back to browser-origin defaults.',
      attempts.map((attempt) => ({
        url: attempt.url,
        error: attempt.error,
      }))
    );
  };
}
